import { DEFAULT_CHANNEL_TAB, type ChannelTab } from '../channel/tabs.ts';
import { youtubeRouteUrl } from './external.ts';
import { channelRoute, parseRoute, type Route } from './routes.ts';

/**
 * §8's **inbound link resolution** — the half of the URL scheme that cannot be answered by a pure
 * function, plus the paste-a-link reader that decides when it has to be asked.
 *
 * Two endpoints, and the division of labour between them and {@link parseRoute} is the whole design:
 *
 * | Form | Answered by | Why |
 * |---|---|---|
 * | `/watch?v=` · `/shorts/ID` · `/live/ID` · `/embed/ID` · `/watch/ID` · bare `/ID` · `youtu.be/ID` | `parseRoute` | the id is in the URL |
 * | `/playlist?list=` · `/channel/UCID` · `/results?…` · `/hashtag/…` · `/feed/…` | `parseRoute` | ditto |
 * | `/@handle` · `/c/NAME` · `/user/NAME` · bare `/NAME` | `/api/v1/resolveurl` | a name is not an id |
 * | `/clip/ID` | `/api/v1/clips/{id}` | the start time is protobuf inside a base64 blob |
 *
 * **Measured 2026-07-29 against `invidious.example.com`, and four of the measurements shape the code:**
 *
 * 1. **`/api/v1/resolveurl` only accepts an absolute `youtube.com` URL.** A bare `/@handle`,
 *    `@handle`, `/c/NAME` and `/user/NAME` all answer `400 {"error":"Unknown url"}`, as do
 *    `youtu.be/ID` and the instance's *own* watch URL. So the parameter is built with
 *    {@link youtubeRouteUrl} — §8's premise that our paths are YouTube's, used as an input builder.
 *    **The spelling it is asked in matters** (measured 2026-09-06): `youtube.com/PewDiePie` and
 *    `youtube.com/user/PewDiePie` both answer the UCID while `youtube.com/c/PewDiePie` answers a
 *    relayed `404`, and `vsauce` behaves the same way. So the bare form has to stay bare all the way
 *    to the endpoint, which is what `ChannelNameForm`'s fourth member is for.
 * 2. **It can never resolve a Shorts URL.** A real Short answers `200
 *    {"params":null,"pageType":"WEB_PAGE_TYPE_SHORTS"}` with **no `videoId`**; a `/shorts/` URL
 *    carrying an ordinary video id answers `400 Unknown url`. `parseRoute` rewrites those locally.
 * 3. **An unrecognised URL succeeds.** `url=` empty, and a clip id that does not exist, both answer
 *    `200 {"browseId":"FEwhat_to_watch","pageType":"WEB_PAGE_TYPE_BROWSE"}` — YouTube's home feed
 *    wearing a 200. That is why {@link ResolvedTarget} has an `unrecognised` arm instead of the parser
 *    reaching for `browseId` whenever a channel id is missing.
 * 4. **A name that does not exist is a relayed 404 at `500`.** `/@ThisHandleDoesNotExistAtAll99`
 *    answers `500 {"error":"Error: non 200 status code. Youtube API returned status code 404…"}`, which
 *    `relayedUpstreamStatus` already reads as `video-unavailable` — so "no such channel" is
 *    a sentence the app can say without a new classifier arm.
 */

/** `/api/v1/resolveurl?url=<absolute youtube.com URL>`. */
export const RESOLVE_URL_PATH = '/api/v1/resolveurl';

export function clipPath(clipId: string): string {
  return `/api/v1/clips/${encodeURIComponent(clipId)}`;
}

/**
 * The URL to hand `/api/v1/resolveurl` for a route that names a channel, or `null` for any other
 * route.
 *
 * Narrow on purpose. The endpoint *will* answer a watch or playlist URL, but every one of those forms
 * already parses locally and for free — asking the network to read a URL we can read ourselves would
 * add a request, a failure mode and a 400-on-`youtu.be` trap to the most common inbound link in the
 * app. The only thing it knows that we cannot is the name → UCID mapping.
 */
export function channelLookupUrl(route: Route): string | null {
  return route.kind === 'channelName' ? youtubeRouteUrl(route) : null;
}

/**
 * What `/api/v1/resolveurl` said, as data. The four arms are the four `pageType` families that the
 * measurements above turned up; `unrecognised` carries the raw string so a screen can name it rather
 * than guessing.
 */
export type ResolvedTarget =
  | {
      readonly kind: 'video';
      readonly videoId: string;
      /** `startTimeSeconds` — the endpoint parses `t=1h2m3s` into `3723` itself. */
      readonly startSec: number | null;
      /** `playlistId`, present when the resolved URL carried a `list`. */
      readonly playlistId: string | null;
    }
  | { readonly kind: 'channel'; readonly channelId: string }
  | { readonly kind: 'playlist'; readonly playlistId: string }
  /**
   * `WEB_PAGE_TYPE_SHORTS` (no id), `WEB_PAGE_TYPE_SEARCH` (no query), `WEB_PAGE_TYPE_BROWSE`
   * (`FEhashtag`, `FEwhat_to_watch`) — a 200 that identifies nothing we can navigate to.
   */
  | { readonly kind: 'unrecognised'; readonly pageType: string };

/**
 * Where a resolution lands. `want.tab` is carried through from the route that asked, so
 * `/@handle/shorts` resolves to that channel's Shorts tab rather than dropping the reader on Videos.
 *
 * `null` for `unrecognised`, which is the honest answer: the instance replied successfully and what it
 * replied does not name a page.
 */
export function routeForResolvedTarget(
  target: ResolvedTarget,
  want: { readonly tab?: ChannelTab } = {},
): Route | null {
  switch (target.kind) {
    case 'video':
      return {
        kind: 'watch',
        videoId: target.videoId,
        startSec: target.startSec,
        playlistId: target.playlistId,
        index: null,
      };
    case 'channel':
      return channelRoute(target.channelId, { tab: want.tab ?? DEFAULT_CHANNEL_TAB });
    case 'playlist':
      return { kind: 'playlist', playlistId: target.playlistId };
    case 'unrecognised':
      return null;
  }
}

/**
 * A clip, as `/api/v1/clips/{id}` reports it. The times are **fractional seconds** — measured
 * `startTime: 89.186`, `endTime: 98.954` — so they are kept as given here and rounded only where a URL
 * needs an integer ({@link clipWatchRoute}).
 *
 * Both times are nullable in the endpoint's own source: they are only filled in when YouTube's
 * response carried the `params` blob they are decoded from.
 */
export interface ClipTarget {
  readonly videoId: string;
  readonly startSec: number | null;
  readonly endSec: number | null;
  /** `clipTitle` — the clipper's caption, not the video's title. */
  readonly title: string | null;
}

/**
 * §8 asks for a clip to resolve to `/watch?v=…` "with the clip's start/end". **Only the start
 * survives**, and that is a property of the scheme rather than a shortcut: `/watch` has no end
 * parameter in YouTube's URL scheme or ours (`end=` exists on `/embed` only), so carrying one would
 * mean inventing a parameter and breaking the domain-swap property the whole section exists for. The
 * end time is reported on the way through instead, where a reader can see it.
 *
 * Floored rather than rounded, and clamped at zero: seeking a fraction of a second *before* the clip
 * shows a frame that belongs to it, seeking after skips one.
 */
export function clipWatchRoute(clip: ClipTarget): Route {
  return {
    kind: 'watch',
    videoId: clip.videoId,
    startSec: clip.startSec === null ? null : Math.max(0, Math.floor(clip.startSec)),
    playlistId: null,
    index: null,
  };
}

/**
 * Anything a human might paste, read as a route — §8's paste-a-link affordance.
 *
 * It is thin, and that is the point: `parseRoute` ignores the origin by design, so every YouTube,
 * Invidious and Visious URL of a form the scheme knows is already covered, and this adds only the two
 * things a *pasted* string has that an in-app href does not.
 *
 * 1. **A missing scheme.** `youtu.be/ID` and `www.youtube.com/watch?v=ID` are what a browser's address
 *    bar hands over on copy. Without a scheme `parseUrl` reads the host as the first path segment, so
 *    `youtu.be/ID` would parse as a two-segment path and fail.
 * 2. **Nothing else.** A bare 11-character id needs no help — it lands on §8's bare-ID rule, which
 *    exists precisely because that is what `youtu.be` puts in a path.
 *
 * `null` means "not something this build can open", and the caller says so rather than guessing: a
 * pasted URL that resolves to the wrong page is worse than one that is refused.
 *
 * **This is not the function to hand raw text to.** Two of the scheme's rules read a bare word as an
 * address — eleven id characters are a video, and three to thirty name characters are a channel to
 * look up — so "programming" opens a watch page here and "javascript" starts a lookup. That is right
 * for a string a reader pasted meaning "open this", and wrong for anything typed. {@link
 * readPastedAddress} is the one to ask when the text might be a search.
 */
export function readPastedLink(text: string): Route | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const route = parseRoute(withInferredScheme(trimmed));
  return route.kind === 'not-found' ? null : route;
}

/**
 * {@link readPastedLink} minus the bare-ID rule — for the header *search* box, which is where
 * the paste-a-link affordance lives now that Home's box is retired.
 *
 * The difference decides whether search still works at all, because two of the scheme's rules read a
 * bare word as an address. The URL scheme reads any 11-character token as a video id, since that is
 * what a `youtu.be` path carries — but "programming" is eleven characters too, and a search box that
 * opens a dead watch page for a word has guessed wrong in the worst way. Since 2026-09-06 a word of
 * three to thirty name characters is a channel to look up as well, which would turn every other search
 * into a request to the instance. So text counts as a link only when it is *shaped like an address* —
 * a scheme, a leading host, a path, or a handle — and everything else stays a search. A bare id, and a
 * bare name, still open when pasted as any of their URL forms.
 */
export function readPastedAddress(text: string): Route | null {
  const trimmed = text.trim();
  const addressShaped =
    HAS_SCHEME_RE.test(trimmed) ||
    LEADING_HOST_RE.test(trimmed) ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('@');
  return addressShaped ? readPastedLink(trimmed) : null;
}

const HAS_SCHEME_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//;
/**
 * A leading `host.tld` followed by a path, a query, a fragment or the end of the string. The final
 * label is required so an id can never be mistaken for a host — no video id, handle or clip id
 * contains a `.` followed by two letters at the end of a token.
 */
const LEADING_HOST_RE = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(?=[/?#]|$)/i;

function withInferredScheme(text: string): string {
  if (HAS_SCHEME_RE.test(text)) return text;
  return LEADING_HOST_RE.test(text) ? `https://${text}` : text;
}
